import { TrendingUp } from "lucide-react";

// Cores validadas com o script do guia de dataviz nas superfícies reais do app
// (#ffffff claro, #111827 escuro), nos dois modos: banda de luminosidade, piso de
// croma, separação para daltonismo e contraste. Vermelho x azul foi o par que
// passou com folga — dois tons quentes juntos (vermelho/laranja, vermelho/amarelo)
// reprovam no piso de visão normal.
const SERIES = {
  score: { light: "#4a3aa7", dark: "#9085e9" },
  critical: { light: "#d03b3b", dark: "#e66767" },
  dissatisfied: { light: "#2a78d6", dark: "#3987e5" },
};

const WIDTH = 560;
const HEIGHT = 130;
const PAD = { top: 12, right: 16, bottom: 22, left: 30 };
const PLOT_W = WIDTH - PAD.left - PAD.right;
const PLOT_H = HEIGHT - PAD.top - PAD.bottom;

const weekLabel = (iso) =>
  new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });

const Swatch = ({ variable, label }) => (
  <span className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
    <span
      className="h-2.5 w-2.5 rounded-sm"
      style={{ backgroundColor: `var(${variable})` }}
    />
    {label}
  </span>
);

// Série única: sem legenda, o título nomeia o dado. O último ponto leva rótulo
// direto para o número não depender de passar o mouse.
const ScoreChart = ({ weeks }) => {
  const points = weeks
    .map((week, index) => ({ week, index }))
    .filter(({ week }) => week.agentScore !== null);

  if (points.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-gray-500 dark:text-gray-400">
        Sem nota registrada no período.
      </p>
    );
  }

  const x = (index) =>
    PAD.left + (weeks.length === 1 ? PLOT_W / 2 : (index / (weeks.length - 1)) * PLOT_W);
  const y = (value) => PAD.top + PLOT_H - (value / 10) * PLOT_H;
  const last = points[points.length - 1];

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="h-auto w-full"
      role="img"
      aria-label="Nota média do atendimento por semana"
    >
      {[0, 5, 10].map((tick) => (
        <g key={tick}>
          <line
            x1={PAD.left}
            x2={WIDTH - PAD.right}
            y1={y(tick)}
            y2={y(tick)}
            className="stroke-gray-200 dark:stroke-gray-700"
            strokeWidth="1"
          />
          <text
            x={PAD.left - 6}
            y={y(tick) + 3}
            textAnchor="end"
            className="fill-gray-400 text-[9px]"
          >
            {tick}
          </text>
        </g>
      ))}

      <polyline
        fill="none"
        stroke="var(--trend-score)"
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
        points={points.map(({ week, index }) => `${x(index)},${y(week.agentScore)}`).join(" ")}
      />

      {points.map(({ week, index }) => (
        <circle
          key={week.weekStart}
          cx={x(index)}
          cy={y(week.agentScore)}
          r="4"
          fill="var(--trend-score)"
          stroke="var(--trend-surface)"
          strokeWidth="2"
        >
          <title>
            {`Semana de ${weekLabel(week.weekStart)} · nota ${week.agentScore
              .toFixed(1)
              .replace(".", ",")} · ${week.analyses} análises`}
          </title>
        </circle>
      ))}

      <text
        x={Math.max(PAD.left, Math.min(x(last.index), WIDTH - PAD.right - 8))}
        y={Math.max(PAD.top + 8, y(last.week.agentScore) - 10)}
        textAnchor="middle"
        className="fill-gray-700 text-[11px] font-semibold dark:fill-gray-200"
      >
        {last.week.agentScore.toFixed(1).replace(".", ",")}
      </text>

      {weeks.map((week, index) =>
        index === 0 || index === weeks.length - 1 ? (
          <text
            key={week.weekStart}
            x={x(index)}
            y={HEIGHT - 6}
            textAnchor={index === 0 ? "start" : "end"}
            className="fill-gray-400 text-[9px]"
          >
            {weekLabel(week.weekStart)}
          </text>
        ) : null,
      )}
    </svg>
  );
};

// Duas séries de contagem: barras agrupadas com 2px de respiro entre elas.
const CountsChart = ({ weeks }) => {
  const max = Math.max(
    1,
    ...weeks.map((week) => Math.max(week.highRisk || 0, week.dissatisfied || 0)),
  );
  const slot = PLOT_W / weeks.length;
  const barWidth = Math.max(3, Math.min(14, slot / 2 - 3));
  const y = (value) => PAD.top + PLOT_H - (value / max) * PLOT_H;

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="h-auto w-full"
      role="img"
      aria-label="Conversas críticas e clientes insatisfeitos por semana"
    >
      <line
        x1={PAD.left}
        x2={WIDTH - PAD.right}
        y1={PAD.top + PLOT_H}
        y2={PAD.top + PLOT_H}
        className="stroke-gray-300 dark:stroke-gray-600"
        strokeWidth="1"
      />
      <text x={PAD.left - 6} y={PAD.top + 8} textAnchor="end" className="fill-gray-400 text-[9px]">
        {max}
      </text>

      {weeks.map((week, index) => {
        const center = PAD.left + slot * index + slot / 2;
        const bars = [
          { key: "critical", value: week.highRisk || 0, color: "var(--trend-critical)", label: "risco alto" },
          { key: "dissatisfied", value: week.dissatisfied || 0, color: "var(--trend-dissatisfied)", label: "insatisfeitos" },
        ];

        return (
          <g key={week.weekStart}>
            {bars.map((bar, position) => {
              const height = Math.max(0, PAD.top + PLOT_H - y(bar.value));
              const bx = center + (position === 0 ? -barWidth - 1 : 1);
              return (
                <rect
                  key={bar.key}
                  x={bx}
                  y={y(bar.value)}
                  width={barWidth}
                  height={height}
                  rx="2"
                  fill={bar.color}
                >
                  <title>
                    {`Semana de ${weekLabel(week.weekStart)} · ${bar.value} ${bar.label}`}
                  </title>
                </rect>
              );
            })}
          </g>
        );
      })}

      {weeks.map((week, index) =>
        index === 0 || index === weeks.length - 1 ? (
          <text
            key={week.weekStart}
            x={PAD.left + slot * index + slot / 2}
            y={HEIGHT - 6}
            textAnchor="middle"
            className="fill-gray-400 text-[9px]"
          >
            {weekLabel(week.weekStart)}
          </text>
        ) : null,
      )}
    </svg>
  );
};

const AnalysisTrend = ({ trend = [] }) => {
  if (!trend.length) return null;

  return (
    <div
      className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-colors dark:border-gray-700 dark:bg-gray-900"
      style={{
        "--trend-score": SERIES.score.light,
        "--trend-critical": SERIES.critical.light,
        "--trend-dissatisfied": SERIES.dissatisfied.light,
        "--trend-surface": "#ffffff",
      }}
    >
      <style>{`
        .dark .analysis-trend {
          --trend-score: ${SERIES.score.dark};
          --trend-critical: ${SERIES.critical.dark};
          --trend-dissatisfied: ${SERIES.dissatisfied.dark};
          --trend-surface: #111827;
        }
      `}</style>

      <div className="analysis-trend">
        <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <TrendingUp className="h-4 w-4 text-gray-400" />
          Evolução semanal
        </p>

        {trend.length === 1 && (
          <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
            Só há uma semana com análises até agora — a série se forma conforme a
            rotina roda nas próximas semanas.
          </p>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Nota do atendimento (0 a 10)
            </p>
            <ScoreChart weeks={trend} />
          </div>

          <div>
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Conversas por semana
              </p>
              <div className="flex gap-3">
                <Swatch variable="--trend-critical" label="Risco alto" />
                <Swatch variable="--trend-dissatisfied" label="Insatisfeitos" />
              </div>
            </div>
            <CountsChart weeks={trend} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalysisTrend;
